import React, { useState } from "react";
import "../../../css/Common/Components/ContactUs/ContactUs.css";
import "../../../css/Common/Components/ContactUs/RequestCallback.css";
import NotificationPopup from "../ContactForm/PopUp";
import InputField from "../DesignSystem/InputField";
import CheckBox from "../DesignSystem/Buttons/CheckBox";
import ButtonYellow from "../DesignSystem/Buttons/ButtonYellow";
import { useNavigate } from "react-router-dom";
import { ReactComponent as CloseIcon } from "../../../assets/icons/close.svg";
import { db } from "../../../backend/firebaseConfig";
import { addDoc, collection } from "firebase/firestore";
import notifyRenderError from "frontend/Utils/notifyError";

export const RequestBrochureDetails = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    emailAddress: "",
    privacyPolicy: false,
  });

  /**
   * Validates whether the provided string is a valid email address.
   *
   * Uses a regular expression to check if the input matches the general structure of an email address,
   * including support for quoted strings, domain literals, and standard email formats.
   *
   * @param {string} email - The email address to validate.
   * @returns {boolean} Returns true if the email is valid, false otherwise.
   */
  const isValidEmail = (email) => {
    const emailRegex =
      /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
    // console.log(emailRegex.test(email));
    return emailRegex.test(email);
  };

  const today = new Date();
  const formattedDate = `${today.getDate().toString().padStart(2, "0")}/${(
    today.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}/${today.getFullYear()}`;

  /**
   * Handles the brochure request process:
   * 1. Shows a success popup.
   * 2. Adds the user's name and email to the Firestore "Brochure Requests" collection.
   * 3. Sets a popup message confirming the brochure will be sent.
   * 4. Sends a POST request to the backend to trigger the brochure email.
   * 5. Resets the form data.
   *
   * @async
   * @function
   * @param {string} name - The full name of the user requesting the brochure.
   * @param {string} email - The email address to send the brochure to.
   * @returns {Promise<void>} Resolves when the process is complete.
   */
  const requestBrochure = async (name, email) => {
    setPopupType("success");
    setShowPopup(true);
    try {
      await addDoc(
        collection(db, "Enquiries", "Brochure Requests", "Requests"),
        {
          name: name,
          email: email,
          createdAt: formattedDate,
        }
      );
    } catch (error) {
    }

    // setShowPopup(true);
    setPopupMessage(`The brochure will be sent shortly to ${name} at ${email}`);
    // handleClosePopup();
    try {
      const response = await fetch(
        // "http://localhost:5000/send-brochure",
        "https://rosemount-financial-solution-ifa-ltd.onrender.com/send-brochure",

        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, name: name.split(" ")[0] }),
        }
      );
      const result = await response.json();
      if (response.ok) {
      } else {
      }
    } catch (error) {
      try {
        await notifyRenderError(error, "NETWORK_ERROR");
      } catch (e) {}
    }

    setFormData({
      name: "",
      emailAddress: "",
      privacyPolicy: false,
    });
    // handleClosePopup();
  };

  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [showPopup, setShowPopup] = useState(false); // Popup visibility
  const [popupMessage, setPopupMessage] = useState(""); // Popup message
  const [popupType, setPopupType] = useState("success"); // Popup type

  /**
   * Validates the form data and returns an object containing any validation errors.
   *
   * Checks for the following:
   * - Name field must not be empty.
   * - Email Address field must not be empty and must be a valid email format.
   * - Privacy Policy checkbox must be accepted.
   *
   * @returns {Object} An object where keys are field names and values are error messages for invalid fields.
   */
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors["name"] = "Name is required.";
    if (!formData.emailAddress.trim())
      newErrors["emailAddress"] = "Email Address is required.";

    if (formData.emailAddress.trim() && !isValidEmail(formData.emailAddress))
      newErrors["emailAddress"] = "Please enter a valid email address.";
    else if (!formData.emailAddress.trim())
      newErrors["emailAddress"] = "Email Address is required.";
    if (formData.privacyPolicy === false)
      newErrors["privacyPolicy"] = "You must accept the privacy policy.";

    return newErrors;
  };

  /**
   * Handles changes to input fields in the form.
   * Updates the formData state with the new value for the changed input.
   *
   * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>} e - The event object from the input change.
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // validateForm();
  };

  /**
   * Handles the form submission for requesting a brochure.
   * Validates the form data, sets validation errors if any,
   * and triggers the brochure request if validation passes.
   *
   * @async
   * @function handleSubmit
   * @returns {Promise<void>} Resolves when the submission process is complete.
   */
  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    requestBrochure(formData.name, formData.emailAddress);
  };

  /**
   * Handles the closing of the popup by invoking the provided onClose callback.
   * Typically used as an event handler for closing modal dialogs or popups.
   */
  const handleClosePopup = () => {
    onClose();
  };

  /**
   * Handles changes to form input elements and updates the form data state accordingly.
   *
   *   The change event triggered by the form input element.
   *   Extracts the input's name, type, value, and checked properties.
   *   If the input is a checkbox, updates the corresponding form data property with the checked value.
   *   Otherwise, updates the property with the input's value.
   */
  const handleChange = (event) => {
    const { name, type, value, checked } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  return (
    <>
      <form
        className="request-callback-widget request-brochure-widget"
        onSubmit={handleSubmit}
      >
        <div className="close-icon Content" onClick={handleClosePopup}>
          <CloseIcon />
        </div>
        <div className="request-callback-form-container request-brochure-form-container ">
          <div className="form-container">
            <h2 id="callback">Request our Brochure</h2>
            <div className="Content">
              Please provide us with your details, and we will send the brochure
              via email.
            </div>
            <InputField
              title="First Name*"
              placeholder="First Name"
              name="name"
              value={formData.name.trim() ? formData.name : ""}
              onChange={handleChange}
              required={errors["name"] ? true : false}
              isTextarea={false}
            />
            <InputField
              title="Email*"
              placeholder="name@example.com"
              name="emailAddress"
              value={formData.emailAddress.trim() ? formData.emailAddress : ""}
              required={errors["emailAddress"] ? true : false}
              isTextarea={false}
              onChange={handleChange}
            />
            {errors["emailAddress"] ===
              "Please enter a valid email address." && (
              <div className="Error-Message">{errors["emailAddress"]}</div>
            )}
          </div>
          <div className="privacy-policy-container">
            <CheckBox
              name="privacyPolicy"
              error={
                errors["privacyPolicy"] && formData.privacyPolicy === false
                  ? true
                  : false
              }
              checked={formData.privacyPolicy}
              onChange={handleInputChange}
            />
            <div className="Content-SemiBold">
              I have read and understood the{"   "}
              <span
                className="Content-Bold-Underline"
                onClick={() => {
                  navigate("/PrivacyPolicy");
                  // console.log("Privacy Policy clicked");
                }}
              >
                privacy policy
              </span>
            </div>
          </div>
          <ButtonYellow buttonText="SUBMIT" onClick={handleSubmit} />
        </div>
      </form>

      {showPopup && (
        <NotificationPopup
          message={popupMessage}
          type={popupType}
          onClose={() => {
            setShowPopup(false);
            onClose();
          }}
        />
      )}
    </>
  );
};
